// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import type {
  ChunkEntityLink,
  ExtractionAnalysis,
  ExtractionAgent,
  ExtractionDocument,
  ExtractionResult,
  ExtractionBatch,
  ExtractionExport,
  ExtractionWorkflow,
  ConfigEnum,
  PostProcessingHook,
  SchemaDiscovery,
  ShadowComparison,
  TrainingAnalytics,
  FieldMappingResult,
  DocumentStats,
  OperationSuccess,
  PresignedUrl,
} from "../_internal/types.gen";
import {
  // Documents
  getAdminExtractionDocuments,
  getAdminExtractionDocumentsById,
  deleteAdminExtractionDocumentsById,
  getAdminExtractionDocumentsByIdView,
  getAdminExtractionDocumentsByIdStatus,
  patchAdminExtractionDocumentsByIdStatus,
  patchAdminExtractionDocumentsByIdReprocess,
  patchAdminExtractionDocumentsByIdCancel,
  patchAdminExtractionDocumentsByIdDismiss,
  patchAdminExtractionDocumentsByIdExclude,
  patchAdminExtractionDocumentsByIdInclude,
  patchAdminExtractionDocumentsByIdRestore,
  patchAdminExtractionDocumentsByIdVerification,
  patchAdminExtractionDocumentsByIdMarkTrained,
  patchAdminExtractionDocumentsByIdDismissTraining,
  patchAdminExtractionDocumentsByIdFinishUpload,
  postAdminExtractionDocumentsBeginUpload,
  postAdminExtractionDocumentsFindOrBeginUpload,
  postAdminDocumentsPresignedUpload,
  postAdminExtractionDocumentsUpload,
  postAdminExtractionDocumentsBulkReprocess,
  postAdminDocumentsBulkDelete,
  getAdminDocumentsStats,
  getAdminExtractionDocumentsWorkspaceThroughput,
  getAdminExtractionDocumentsWorkspaceByWorkspaceId,
  getAdminExtractionDocumentsWorkspaceByWorkspaceIdByStatusByStatus,
  getAdminExtractionDocumentsWorkspaceByWorkspaceIdReviewQueue,
  getAdminExtractionDocumentsWorkspaceByWorkspaceIdTrained,
  getAdminExtractionDocumentsWorkspaceByWorkspaceIdTrashed,
  getAdminExtractionDocumentsWorkspaceByWorkspaceIdExcluded,
  postAdminWorkspacesByWorkspaceIdExtractionDocumentsDismissAllTrained,
  // Results
  getAdminExtractionResults,
  getAdminExtractionResultsById,
  patchAdminExtractionResultsById,
  deleteAdminExtractionResultsById,
  getAdminExtractionResultsDocumentByDocumentId,
  getAdminExtractionResultsDocumentByDocumentIdHistory,
  getAdminExtractionResultsDocumentByDocumentIdPartial,
  patchAdminExtractionResultsByIdRegenerate,
  patchAdminExtractionResultsByIdSaveCorrections,
  getAdminExtractionResultsWorkspaceByWorkspaceId,
  // Batches
  getAdminExtractionBatchesById,
  deleteAdminExtractionBatchesById,
  postAdminExtractionBatches,
  getAdminExtractionBatchesWorkspaceByWorkspaceId,
  getAdminExtractionBatchesByIdUploadUrls,
  postAdminExtractionBatchesByIdFinishAll,
  // Exports
  getAdminWorkspacesByWorkspaceIdExtractionExports,
  postAdminWorkspacesByWorkspaceIdExtractionExports,
  getAdminWorkspacesByWorkspaceIdExtractionExportsById,
  // Workflows
  getAdminExtractionWorkflows,
  postAdminExtractionWorkflows,
  getAdminExtractionWorkflowsById,
  patchAdminExtractionWorkflowsById,
  deleteAdminExtractionWorkflowsById,
  // Config enums
  getAdminExtractionConfigEnums,
  postAdminExtractionConfigEnums,
  getAdminExtractionConfigEnumsById,
  patchAdminExtractionConfigEnumsById,
  // Schema discoveries
  postAdminExtractionSchemaDiscoveries,
  getAdminExtractionSchemaDiscoveriesById,
  postAdminExtractionSchemaDiscoveriesBootstrap,
  // Field mappings
  getAdminWorkspacesByWorkspaceIdExtractionByDocumentIdMapping,
  postAdminWorkspacesByWorkspaceIdExtractionByDocumentIdMapping,
  // Shadow comparisons
  getAdminExtractionShadowComparisons,
  getAdminExtractionShadowComparisonsById,
  postAdminExtractionShadowComparisons,
  deleteAdminExtractionShadowComparisonsById,
  getAdminExtractionShadowComparisonsAnalysis,
  // Post-processing hooks
  getAdminPostProcessingHooks,
  getAdminPostProcessingHooksById,
  postAdminPostProcessingHooks,
  patchAdminPostProcessingHooksById,
  deleteAdminPostProcessingHooksById,
  // Training analytics
  getAdminWorkspacesByWorkspaceIdTrainingAnalytics,
  getAdminWorkspacesAnalyticsBatch,
  // Extraction agents
  getAdminExtractionAgents,
  getAdminExtractionAgentsById,
  postAdminExtractionAgentsPredict,
  // Extraction analyses
  getAdminExtractionAnalyses,
  getAdminExtractionAnalysesById,
  // Chunk entity links
  getAdminExtractionChunkEntityLinks,
  getAdminExtractionChunkEntityLinksById,
  getAdminExtractionChunkEntityLinksDocumentByDocumentId,
  getAdminExtractionChunkEntityLinksChunkByDocumentChunkId,
  getAdminExtractionChunkEntityLinksEntityByGraphNodeId,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

// ---------------------------------------------------------------------------
// Shared shape aliases (F-C7 — give named shape to dynamic arrays)
// ---------------------------------------------------------------------------

/**
 * A single field correction submitted to `results.saveCorrections`.
 *
 * `value` is intentionally unconstrained — agent schemas decide the type.
 */
export interface ExtractionCorrectionRow {
  field?: string;
  name?: string;
  value?: unknown;
  notes?: string;
}

/**
 * A single source→target column mapping for `fieldMappings.create`.
 *
 * Admin callers typically use the `source`/`target` pair; `source_column` /
 * `target_field` are accepted as aliases.
 */
export interface FieldMappingEntry {
  source_column?: string;
  target_field?: string;
  source?: string;
  target?: string;
  confidence?: number;
}

/**
 * A single step in an `ExtractionWorkflow.steps` array.
 *
 * Shape is intentionally loose — user-authored `config` values depend on
 * step type and are server-validated.
 */
export interface ExtractionWorkflowStep {
  name?: string;
  type?: string;
  agent_id?: string;
  agent_source?: string;
  config?: Record<string, unknown>;
  order?: number;
  outputs?: string[];
}

/**
 * A single inferred field returned by schema discovery.
 *
 * `examples` mirror whatever the LLM extracted — no narrowing.
 */
export interface SchemaDiscoveryField {
  name?: string;
  field_type?: string;
  description?: string;
  examples?: unknown[];
  confidence?: number;
}

/**
 * A single row inside `ExtractionResult.rows` (tabular extraction output).
 *
 * Keys are user-defined field names.
 */
export type ExtractionRowResultRow = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Typed attribute interfaces (F-C3 — no catch-all index signatures)
// ---------------------------------------------------------------------------

/**
 * Attributes for updating document processing status.
 *
 * Mirrors `Document.update_status` accept list: `status`, `progress`,
 * `error_message`, `pages`, `billed_credits`, `domain`, `document_type`,
 * `municipality`, `state`, `classification_confidence`, `avg_confidence`,
 * `completed_at`, `processed_at`, `training_metadata`, `agent_id`,
 * `agent_version_id`, `schema_revision`, `char_count`, `word_count`,
 * `line_count`, `image_width`, `image_height`.
 */
export interface UpdateDocumentStatusAttributes {
  status: string;
  progress?: number;
  error_message?: string | null;
  pages?: number;
  billed_credits?: number;
  domain?: string;
  document_type?: string;
  municipality?: string;
  state?: string;
  classification_confidence?: number;
  avg_confidence?: number;
  completed_at?: string;
  processed_at?: string;
  training_metadata?: Record<string, unknown>;
  agent_id?: string;
  agent_version_id?: string;
  schema_revision?: number;
  char_count?: number;
  word_count?: number;
  line_count?: number;
  image_width?: number;
  image_height?: number;
}

/**
 * Attributes for updating document verification.
 *
 * Mirrors `Document.update_verification` accept list.
 */
export interface UpdateDocumentVerificationAttributes {
  verification_status?: "partially_verified" | "fully_verified" | string;
  verified_by_user_id?: string;
  last_verified_at?: string;
  avg_confidence?: number;
}

/**
 * Attributes for beginning a document upload.
 *
 * Mirrors `Document.begin_upload` accept list + `content_type` action
 * argument.
 */
export interface AdminBeginUploadAttributes {
  filename: string;
  file_type: string;
  workspace_id: string;
  content_type?: string;
  file_size_bytes?: number;
  file_hash?: string;
  parent_document_id?: string;
  batch_id?: string;
}

/**
 * Attributes for finding or beginning a document upload.
 *
 * Mirrors `Document.find_or_begin_upload` action arguments.
 */
export interface AdminFindOrBeginUploadAttributes {
  filename: string;
  file_type: string;
  workspace_id: string;
  file_hash?: string;
  file_size_bytes?: number;
  content_type?: string;
  parent_document_id?: string;
  batch_id?: string;
  force?: boolean;
}

/**
 * Attributes for direct document upload.
 *
 * Mirrors `Document.upload` accept list.
 */
export interface AdminUploadDocumentAttributes {
  filename: string;
  file_type: string;
  workspace_id: string;
  storage_path?: string;
  file_size_bytes?: number;
  file_hash?: string;
  parent_document_id?: string;
  bucket_name?: string;
}

/**
 * Request attributes for bulk-reprocessing multiple documents (admin).
 *
 * Mirrors `BulkReprocessResult.:bulk_reprocess` arguments. `workspace_id`
 * and `document_ids` are both required; the server returns a 400 otherwise.
 */
export interface AdminBulkReprocessAttributes {
  workspace_id: string;
  document_ids: string[];
  schema_version_id?: string;
}

export type AdminWorkspaceThroughputWindow = "today" | "7d" | "30d";

export type AdminWorkspaceThroughputBucket = {
  start_at: string;
  end_at: string;
  count: number;
  unit: "hour" | "day";
};

export type AdminWorkspaceThroughput = {
  metrics: {
    decided: number;
    completed: number;
    failed: number;
    median_cycle_seconds: number | null;
  };
  buckets: AdminWorkspaceThroughputBucket[];
};

/** Attributes for requesting a presigned upload URL. */
export interface AdminPresignedUploadAttributes {
  filename: string;
  mime_type: string;
  size_bytes: number;
  workspace_id: string;
}

/** Result of a batch finish-all operation. */
export interface AdminBatchFinishAllResult {
  processed_count?: number;
  results?: Record<string, unknown>;
}

/**
 * Attributes for updating an extraction result.
 *
 * Mirrors the `ExtractionResult :update` accept list: `status`,
 * `credits_used`, `processing_time_ms`, `extracted_fields`,
 * `classification_confidence`, `domain`, `document_type`, `municipality`,
 * `state`. `review_status` is the UI-facing alias.
 */
export interface AdminUpdateResultAttributes {
  review_status?: string;
  status?: string;
  classification_confidence?: number;
  domain?: string;
  document_type?: string;
  municipality?: string;
  state?: string;
}

/**
 * Attributes for regenerating an extraction result.
 *
 * Mirrors the `:regenerate` action arguments: `feedback`, `fields_to_retry`,
 * `schema_version_id`.
 */
export interface AdminRegenerateResultAttributes {
  feedback?: string;
  fields_to_retry?: string[];
  schema_version_id?: string;
}

/** Attributes for saving corrections to an extraction result. */
export interface AdminSaveCorrectionsAttributes {
  corrections: ExtractionCorrectionRow[];
}

/**
 * Attributes for creating an extraction batch.
 *
 * Mirrors `Batch :create` accept list (`workspace_id`, `name`, `user_label`).
 * `agent_id` and `document_ids` are accepted as supplemental server-side
 * arguments in some deployments.
 */
export interface AdminCreateBatchAttributes {
  name?: string;
  user_label?: string;
  agent_id?: string;
  workspace_id: string;
  document_ids?: string[];
}

/**
 * Attributes for creating an extraction export.
 *
 * Mirrors `Export :create` accept list (`workspace_id`, `format`, `config`).
 */
export interface AdminCreateExportAttributes {
  format: "csv" | "json" | "xlsx";
  agent_id?: string;
  config?: Record<string, unknown>;
}

/**
 * Attributes for creating an extraction workflow.
 *
 * Mirrors `ExtractionWorkflow :create` accept list: `name`, `description`,
 * `trigger`, `steps`, `workspace_id`, `enabled`.
 */
export interface AdminCreateWorkflowAttributes {
  name: string;
  description?: string;
  trigger?: "on_upload" | "on_schedule" | "manual";
  steps?: ExtractionWorkflowStep[];
  workspace_id?: string;
  enabled?: boolean;
}

/**
 * Attributes for updating an extraction workflow.
 *
 * Mirrors `ExtractionWorkflow :update` accept list.
 */
export interface AdminUpdateWorkflowAttributes {
  name?: string;
  description?: string;
  trigger?: "on_upload" | "on_schedule" | "manual";
  steps?: ExtractionWorkflowStep[];
  enabled?: boolean;
}

/**
 * Attributes for creating a config enum.
 *
 * Mirrors `ConfigEnum :create` accept list: `type`, `value`, `label`,
 * `description`, `is_active`, `sort_order`.
 */
export interface AdminCreateConfigEnumAttributes {
  type: string;
  value: string;
  label?: string;
  description?: string;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * Attributes for updating a config enum.
 *
 * Mirrors `ConfigEnum :update` accept list: `label`, `description`,
 * `is_active`, `sort_order`.
 */
export interface AdminUpdateConfigEnumAttributes {
  label?: string;
  description?: string;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * Attributes for creating a schema discovery.
 *
 * Mirrors the three `:create` variants in `SchemaDiscovery`: combinations of
 * `agent_id`, `document_id`, `workspace_id`, `advanced_mode`,
 * `discovered_fields`, `suggested_name`, `source`.
 */
export interface AdminCreateSchemaDiscoveryAttributes {
  agent_id?: string;
  document_id?: string;
  workspace_id?: string;
  advanced_mode?: boolean;
  suggested_name?: string;
  discovered_fields?: SchemaDiscoveryField[];
  source?: string;
}

/** Attributes for bootstrapping schema discovery. */
export interface AdminBootstrapSchemaDiscoveryAttributes {
  workspace_id?: string;
  advanced_mode?: boolean;
  source?: string;
}

/**
 * Attributes for creating/updating field mappings.
 *
 * Mirrors `FieldMappingConfirmation :confirm` accept list:
 * `mappings`, `add_to_schema`, `ignored_fields`, `confirmed`.
 */
export interface AdminFieldMappingAttributes {
  confirmed: boolean;
  mappings: FieldMappingEntry[];
  add_to_schema?: FieldMappingEntry[];
  ignored_fields?: string[];
}

/**
 * Attributes for creating a post-processing hook.
 *
 * Mirrors `PostProcessingHook :create` accept list.
 */
export interface AdminCreatePostProcessingHookAttributes {
  name: string;
  trigger?: "after_extraction" | "after_validation" | "before_downstream";
  type?: "webhook" | "oban_worker" | "reactor_step";
  config?: Record<string, unknown>;
  field_filter?: string[];
  can_modify?: boolean;
  order?: number;
  enabled?: boolean;
  workspace_id: string;
  application_id?: string;
}

/**
 * Attributes for updating a post-processing hook.
 *
 * Mirrors `PostProcessingHook :update` accept list.
 */
export interface AdminUpdatePostProcessingHookAttributes {
  name?: string;
  trigger?: "after_extraction" | "after_validation" | "before_downstream";
  type?: "webhook" | "oban_worker" | "reactor_step";
  config?: Record<string, unknown>;
  field_filter?: string[];
  can_modify?: boolean;
  order?: number;
  enabled?: boolean;
}

/** Attributes for predicting the best extraction agent for a document. */
export interface AdminPredictExtractionAgentAttributes {
  name?: string;
  description?: string;
  file_content?: string;
  document_id?: string;
}

/**
 * Attributes for creating a shadow comparison.
 *
 * Mirrors `ShadowComparison :create` accept list. `document_id` and
 * `workspace_id` are the conventional seed inputs; other fields are
 * computed server-side.
 */
export interface AdminCreateShadowComparisonAttributes {
  document_id?: string;
  workspace_id?: string;
  mime_type?: string;
  filename?: string;
  file_size?: number;
  page_count?: number;
  primary_extractor?: string;
  shadow_extractor?: string;
}

// ---------------------------------------------------------------------------
// Namespace
// ---------------------------------------------------------------------------

/**
 * Admin extraction namespace — full document processing pipeline management.
 *
 * Sub-namespaces: documents, results, batches, exports, workflows,
 * configEnums, schemaDiscoveries, fieldMappings, postProcessingHooks,
 * trainingAnalytics, extractionAgents.
 *
 * @example
 * ```typescript
 * const admin = new GptAdmin({ apiKey: 'sk_srv_...' });
 *
 * // List documents for a workspace
 * const docs = await admin.extraction.documents.listByWorkspace(workspaceId);
 *
 * // Get extraction result history
 * const history = await admin.extraction.results.history(documentId);
 *
 * // Create a batch upload
 * const batch = await admin.extraction.batches.create({ workspace_id: '...' });
 * ```
 */
export function createExtractionNamespace(rb: RequestBuilder) {
  return {
    /** Document lifecycle — upload, process, review, train, bulk operations. */
    documents: {
      /**
       * Lists all extraction documents across the platform.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction document objects.
       *
       * @example
       * ```typescript
       * const docs = await admin.extraction.documents.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocuments,
          {},
          options,
        );
      },

      /**
       * Retrieves a single extraction document by its unique identifier.
       *
       * @param id - The UUID of the document to retrieve.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction document object.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.get('doc-uuid');
       * console.log(doc.status, doc.filename);
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          getAdminExtractionDocumentsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Permanently deletes an extraction document and its associated results.
       *
       * @param id - The UUID of the document to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns `true` when the deletion succeeds.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.delete('doc-uuid');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminExtractionDocumentsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Retrieves the rendered view of a document, including page images and
       * overlay data for visual inspection.
       *
       * @param id - The UUID of the document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The document object with rendered view data.
       *
       * @example
       * ```typescript
       * const view = await admin.extraction.documents.view('doc-uuid');
       * ```
       */
      view: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          getAdminExtractionDocumentsByIdView,
          { path: { id } },
          options,
        );
      },

      /**
       * Retrieves the current processing status of a document, including
       * progress percentage and any error details.
       *
       * @param id - The UUID of the document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The document object with current status fields.
       *
       * @example
       * ```typescript
       * const { status, progress_percent } = await admin.extraction.documents.status('doc-uuid');
       * console.log(`Status: ${status} (${progress_percent}%)`);
       * ```
       */
      status: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          getAdminExtractionDocumentsByIdStatus,
          { path: { id } },
          options,
        );
      },

      /**
       * Updates the processing status of a document. Used by admin tooling to
       * manually transition a document between pipeline states.
       *
       * @param id - The UUID of the document to update.
       * @param attributes - The status attributes to set.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.updateStatus('doc-uuid', {
       *   status: 'completed',
       * });
       * ```
       */
      updateStatus: async (
        id: string,
        attributes: UpdateDocumentStatusAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdStatus,
          {
            path: { id },
            body: {
              data: { id, type: "extraction-document", attributes },
            },
          },
          options,
        );
      },

      /**
       * Reprocesses a completed or failed document through the extraction
       * pipeline from scratch.
       *
       * @param id - The UUID of the document to reprocess.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The document object with its new processing status.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.reprocess('doc-uuid');
       * console.log(`Reprocessing: ${doc.status}`);
       * ```
       */
      reprocess: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdReprocess,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Cancels a document that is currently queued or being processed.
       *
       * @param id - The UUID of the document to cancel.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The document object with its updated (cancelled) status.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.cancel('doc-uuid');
       * ```
       */
      cancel: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdCancel,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Dismisses a document from the review queue without changing its
       * underlying data or verification status.
       *
       * @param id - The UUID of the document to dismiss.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.dismiss('doc-uuid');
       * ```
       */
      dismiss: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdDismiss,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Excludes a document from the training dataset. Excluded documents are
       * not used when retraining extraction models.
       *
       * @param id - The UUID of the document to exclude.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.exclude('doc-uuid');
       * ```
       */
      exclude: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdExclude,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Re-includes a previously excluded document in the training dataset.
       *
       * @param id - The UUID of the document to include.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.include('doc-uuid');
       * ```
       */
      include: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdInclude,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Restores a soft-deleted (trashed) document back to active status.
       *
       * @param id - The UUID of the document to restore.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The restored extraction document object.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.restore('doc-uuid');
       * console.log(`Restored: ${doc.filename}`);
       * ```
       */
      restore: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdRestore,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Updates the verification status of a document. Used during the human
       * review step to confirm or reject extracted data.
       *
       * @param id - The UUID of the document to update.
       * @param attributes - Verification attributes to set.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.updateVerification('doc-uuid', {
       *   verification_status: 'verified',
       *   avg_confidence: 0.95,
       * });
       * ```
       */
      updateVerification: async (
        id: string,
        attributes: UpdateDocumentVerificationAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdVerification,
          {
            path: { id },
            body: {
              data: { id, type: "extraction-document", attributes },
            },
          },
          options,
        );
      },

      /**
       * Marks a document as trained, indicating its extraction results have been
       * incorporated into the model training set.
       *
       * @param id - The UUID of the document to mark as trained.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.markTrained('doc-uuid');
       * ```
       */
      markTrained: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdMarkTrained,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Dismisses the training flag on a document without un-training it.
       * Useful for clearing training notifications in the review UI.
       *
       * @param id - The UUID of the document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction document object.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.dismissTraining('doc-uuid');
       * ```
       */
      dismissTraining: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdDismissTraining,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Signals the server that a multi-part upload has completed and the
       * document is ready for processing.
       *
       * @param id - The UUID of the document whose upload is being finalized.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The document object transitioned to processing state.
       *
       * @example
       * ```typescript
       * await admin.extraction.documents.finishUpload('doc-uuid');
       * ```
       */
      finishUpload: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        return rb.execute<ExtractionDocument>(
          patchAdminExtractionDocumentsByIdFinishUpload,
          { path: { id }, body: { data: { id, type: "extraction-document" } } },
          options,
        );
      },

      /**
       * Begins a new document upload and returns a presigned URL for direct
       * browser-to-storage upload.
       *
       * @param attributes - Upload attributes including the filename and optional metadata.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns A presigned URL object for uploading the file.
       *
       * @example
       * ```typescript
       * const presigned = await admin.extraction.documents.beginUpload({
       *   filename: 'invoice-2026.pdf',
       *   file_type: 'pdf',
       *   workspace_id: 'ws-uuid',
       *   content_type: 'application/pdf',
       * });
       * // Upload file to presigned.upload_url
       * ```
       */
      beginUpload: async (
        attributes: AdminBeginUploadAttributes,
        options?: RequestOptions,
      ): Promise<PresignedUrl> => {
        return rb.execute<PresignedUrl>(
          postAdminExtractionDocumentsBeginUpload,
          {
            body: {
              data: { type: "extraction-document", attributes },
            },
          },
          options,
        );
      },

      /**
       * Finds an existing document by file hash (for deduplication) or begins a
       * new upload if no match is found.
       *
       * @param attributes - Attributes for finding or beginning the upload.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns A presigned URL object (or a reference to the existing document).
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.documents.findOrBeginUpload({
       *   filename: 'invoice-2026.pdf',
       *   file_type: 'pdf',
       *   workspace_id: 'ws-uuid',
       *   file_hash: 'blake3:abc123...',
       *   content_type: 'application/pdf',
       * });
       * ```
       */
      findOrBeginUpload: async (
        attributes: AdminFindOrBeginUploadAttributes,
        options?: RequestOptions,
      ): Promise<PresignedUrl> => {
        return rb.execute<PresignedUrl>(
          postAdminExtractionDocumentsFindOrBeginUpload,
          {
            body: {
              data: { type: "extraction-document", attributes },
            },
          },
          options,
        );
      },

      /**
       * Requests a presigned upload URL for direct S3-compatible upload of a
       * document. Validates file type and size server-side, then returns a URL
       * the client PUTs the raw bytes to. Distinct from `beginUpload`, which
       * creates the Document record up front; `presignedUpload` only returns
       * a URL + storage path without creating the Document row.
       *
       * @param attributes - filename, mime_type, size_bytes (max 50 MB), workspace_id.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns A presigned URL object (`upload_url`, `storage_path`, `expires_in`).
       *
       * @example
       * ```typescript
       * const presigned = await admin.extraction.documents.presignedUpload({
       *   filename: 'invoice.pdf',
       *   mime_type: 'application/pdf',
       *   size_bytes: 512000,
       *   workspace_id: 'ws-uuid',
       * });
       * ```
       */
      presignedUpload: async (
        attributes: AdminPresignedUploadAttributes,
        options?: RequestOptions,
      ): Promise<PresignedUrl> => {
        const { filename, mime_type, size_bytes, workspace_id } = attributes;
        return rb.execute<PresignedUrl>(
          postAdminDocumentsPresignedUpload,
          {
            body: {
              data: {
                type: "presigned-url",
                attributes: { filename, mime_type, size_bytes, workspace_id },
              },
            },
          },
          options,
        );
      },

      /**
       * Uploads a document directly in a single shot. The server handles storage
       * placement and immediately enqueues the document for processing.
       *
       * @param attributes - Upload attributes including filename and optional storage path.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The created extraction document object.
       *
       * @example
       * ```typescript
       * const doc = await admin.extraction.documents.upload({
       *   filename: 'contract.pdf',
       *   file_type: 'pdf',
       *   workspace_id: 'ws-uuid',
       *   storage_path: 'workspaces/ws-uuid/contract.pdf',
       * });
       * ```
       */
      upload: async (
        attributes: AdminUploadDocumentAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionDocument> => {
        const {
          filename,
          file_type,
          workspace_id,
          storage_path,
          file_size_bytes,
          file_hash,
          parent_document_id,
          bucket_name,
        } = attributes;
        return rb.execute<ExtractionDocument>(
          postAdminExtractionDocumentsUpload,
          {
            body: {
              data: {
                type: "extraction-document",
                attributes: {
                  filename,
                  file_type,
                  workspace_id,
                  ...(storage_path !== undefined ? { storage_path } : {}),
                  ...(file_size_bytes !== undefined ? { file_size_bytes } : {}),
                  ...(file_hash !== undefined ? { file_hash } : {}),
                  ...(parent_document_id !== undefined
                    ? { parent_document_id }
                    : {}),
                  ...(bucket_name !== undefined ? { bucket_name } : {}),
                },
              },
            },
          },
          options,
        );
      },

      /**
       * Reprocesses multiple documents in a single bulk operation.
       *
       * @param attrs - Bulk reprocess attributes: `workspace_id` and `document_ids` are required.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An operation success result with the count of enqueued documents.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.documents.bulkReprocess({
       *   workspace_id: 'ws-uuid',
       *   document_ids: ['doc-uuid-1', 'doc-uuid-2'],
       * });
       * console.log(`Enqueued: ${result.enqueued_count}`);
       * ```
       */
      bulkReprocess: async (
        attrs: AdminBulkReprocessAttributes,
        options?: RequestOptions,
      ): Promise<OperationSuccess> => {
        const { workspace_id, document_ids, schema_version_id } = attrs;
        return rb.execute<OperationSuccess>(
          postAdminExtractionDocumentsBulkReprocess,
          {
            body: {
              data: {
                type: "bulk-reprocess-result",
                attributes: {
                  workspace_id,
                  document_ids,
                  ...(schema_version_id ? { schema_version_id } : {}),
                },
              },
            },
          },
          options,
        );
      },

      /**
       * Permanently deletes multiple documents in a single bulk operation.
       *
       * @param ids - An array of document UUIDs to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An operation success result with the count of deleted documents.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.documents.bulkDelete([
       *   'doc-uuid-1',
       *   'doc-uuid-2',
       * ]);
       * console.log(`Deleted: ${result.deleted_count}`);
       * ```
       */
      bulkDelete: async (
        ids: string[],
        options?: RequestOptions,
      ): Promise<OperationSuccess> => {
        return rb.execute<OperationSuccess>(
          postAdminDocumentsBulkDelete,
          {
            body: {
              data: { type: "operation-success", attributes: { ids } },
            },
          },
          options,
        );
      },

      /**
       * Retrieves platform-wide document statistics including counts by status.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns Aggregate document statistics.
       *
       * @example
       * ```typescript
       * const stats = await admin.extraction.documents.stats();
       * console.log(`Total: ${stats.total}, Failed: ${stats.failed}`);
       * ```
       */
      stats: async (options?: RequestOptions): Promise<DocumentStats> => {
        return rb.execute<DocumentStats>(getAdminDocumentsStats, {}, options);
      },

      /**
       * Retrieve throughput metrics and time buckets for a workspace.
       *
       * @param workspaceId - Workspace UUID to scope the admin request.
       * @param window - Optional reporting window. Defaults to `7d` server-side.
       * @param options - Optional request settings.
       * @returns Throughput metrics and completed-document buckets.
       */
      workspaceThroughput: async (
        workspaceId: string,
        window?: AdminWorkspaceThroughputWindow,
        options?: RequestOptions,
      ): Promise<AdminWorkspaceThroughput> => {
        return rb.execute<AdminWorkspaceThroughput>(
          getAdminExtractionDocumentsWorkspaceThroughput,
          {
            query: {
              workspace_id: workspaceId,
              ...(window ? { window } : {}),
            },
          },
          options,
        );
      },

      /**
       * Lists all extraction documents belonging to a specific workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction documents for the workspace.
       *
       * @example
       * ```typescript
       * const docs = await admin.extraction.documents.listByWorkspace('ws-uuid');
       * ```
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocumentsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Lists documents filtered by processing status within a workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param status - The processing status to filter by (e.g. `"completed"`, `"failed"`).
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction documents matching the status filter.
       *
       * @example
       * ```typescript
       * const failed = await admin.extraction.documents.listByStatus('ws-uuid', 'failed');
       * console.log(`${failed.length} failed documents`);
       * ```
       */
      listByStatus: async (
        workspaceId: string,
        status: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocumentsWorkspaceByWorkspaceIdByStatusByStatus,
          { path: { workspace_id: workspaceId, status } },
          options,
        );
      },

      /**
       * Returns the review queue for a workspace — documents awaiting human
       * verification of their extraction results.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of documents in the review queue.
       *
       * @example
       * ```typescript
       * const queue = await admin.extraction.documents.reviewQueue('ws-uuid');
       * console.log(`${queue.length} documents awaiting review`);
       * ```
       */
      reviewQueue: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocumentsWorkspaceByWorkspaceIdReviewQueue,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Lists documents that have been marked as trained within a workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of trained extraction documents.
       *
       * @example
       * ```typescript
       * const trained = await admin.extraction.documents.listTrained('ws-uuid');
       * ```
       */
      listTrained: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocumentsWorkspaceByWorkspaceIdTrained,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Lists soft-deleted (trashed) documents for a workspace. These can be
       * restored via the `restore` method.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of trashed extraction documents.
       *
       * @example
       * ```typescript
       * const trashed = await admin.extraction.documents.listTrashed('ws-uuid');
       * ```
       */
      listTrashed: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocumentsWorkspaceByWorkspaceIdTrashed,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Lists documents that have been excluded from the training dataset
       * within a workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of excluded extraction documents.
       *
       * @example
       * ```typescript
       * const excluded = await admin.extraction.documents.listExcluded('ws-uuid');
       * ```
       */
      listExcluded: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionDocument[]> => {
        return rb.execute<ExtractionDocument[]>(
          getAdminExtractionDocumentsWorkspaceByWorkspaceIdExcluded,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Dismisses all trained-flagged documents in a workspace in one operation.
       * Useful for clearing training notifications after a batch review.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An operation success result with the dismissed count.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.documents.dismissAllTrained('ws-uuid');
       * console.log(`Dismissed ${result.dismissed_count} documents`);
       * ```
       */
      dismissAllTrained: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<OperationSuccess> => {
        return rb.execute<OperationSuccess>(
          postAdminWorkspacesByWorkspaceIdExtractionDocumentsDismissAllTrained,
          {
            path: { workspace_id: workspaceId },
            body: { data: { type: "bulk-dismissal-result" } },
          },
          options,
        );
      },
    },

    /** Extraction result management — CRUD, history, corrections, regeneration. */
    results: {
      /**
       * Lists all extraction results across the platform.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction result objects.
       *
       * @example
       * ```typescript
       * const results = await admin.extraction.results.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ExtractionResult[]> => {
        return rb.execute<ExtractionResult[]>(
          getAdminExtractionResults,
          {},
          options,
        );
      },

      /**
       * Retrieves a single extraction result by its unique identifier.
       *
       * @param id - The UUID of the extraction result.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction result object.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.results.get('result-uuid');
       * console.log(result.extracted_data);
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionResult> => {
        return rb.execute<ExtractionResult>(
          getAdminExtractionResultsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Updates an extraction result's metadata (e.g. review status).
       *
       * @param id - The UUID of the extraction result to update.
       * @param attributes - The attributes to update.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction result object.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.results.update('result-uuid', {
       *   review_status: 'approved',
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdateResultAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionResult> => {
        return rb.execute<ExtractionResult>(
          patchAdminExtractionResultsById,
          {
            path: { id },
            body: {
              data: { id, type: "extraction-result", attributes },
            },
          },
          options,
        );
      },

      /**
       * Permanently deletes an extraction result.
       *
       * @param id - The UUID of the extraction result to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns `true` when the deletion succeeds.
       *
       * @example
       * ```typescript
       * await admin.extraction.results.delete('result-uuid');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminExtractionResultsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Retrieves all extraction results for a specific document.
       *
       * @param documentId - The UUID of the parent document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction results for the document.
       *
       * @example
       * ```typescript
       * const results = await admin.extraction.results.byDocument('doc-uuid');
       * ```
       */
      byDocument: async (
        documentId: string,
        options?: RequestOptions,
      ): Promise<ExtractionResult[]> => {
        return rb.execute<ExtractionResult[]>(
          getAdminExtractionResultsDocumentByDocumentId,
          { path: { document_id: documentId } },
          options,
        );
      },

      /**
       * Retrieves the version history of extraction results for a document,
       * showing how results have changed over reprocessing cycles.
       *
       * @param documentId - The UUID of the parent document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of historical extraction result versions.
       *
       * @example
       * ```typescript
       * const history = await admin.extraction.results.history('doc-uuid');
       * console.log(`${history.length} result versions`);
       * ```
       */
      history: async (
        documentId: string,
        options?: RequestOptions,
      ): Promise<ExtractionResult[]> => {
        return rb.execute<ExtractionResult[]>(
          getAdminExtractionResultsDocumentByDocumentIdHistory,
          { path: { document_id: documentId } },
          options,
        );
      },

      /**
       * Retrieves partial (in-progress) extraction results for a document that
       * is still being processed.
       *
       * @param documentId - The UUID of the parent document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of partial extraction results available so far.
       *
       * @example
       * ```typescript
       * const partial = await admin.extraction.results.partial('doc-uuid');
       * ```
       */
      partial: async (
        documentId: string,
        options?: RequestOptions,
      ): Promise<ExtractionResult[]> => {
        return rb.execute<ExtractionResult[]>(
          getAdminExtractionResultsDocumentByDocumentIdPartial,
          { path: { document_id: documentId } },
          options,
        );
      },

      /**
       * Regenerates an extraction result, optionally incorporating human
       * feedback or restricting which fields to retry.
       *
       * @param id - The UUID of the extraction result to regenerate.
       * @param attributes - Regeneration parameters (feedback, fields to retry).
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The regenerated extraction result object.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.results.regenerate('result-uuid', {
       *   feedback: 'The date field was parsed incorrectly.',
       *   fields_to_retry: ['invoice_date', 'due_date'],
       * });
       * ```
       */
      regenerate: async (
        id: string,
        attributes: AdminRegenerateResultAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionResult> => {
        return rb.execute<ExtractionResult>(
          patchAdminExtractionResultsByIdRegenerate,
          {
            path: { id },
            body: {
              data: { id, type: "extraction-result", attributes },
            },
          },
          options,
        );
      },

      /**
       * Saves manual corrections to an extraction result. The corrections are
       * stored alongside the original data and can be used for model training.
       *
       * @param id - The UUID of the extraction result to correct.
       * @param attributes - The corrections to save.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction result object with corrections applied.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.results.saveCorrections('result-uuid', {
       *   corrections: [
       *     { field: 'invoice_number', value: 'INV-2026-001' },
       *     { field: 'total_amount', value: 1250.00 },
       *   ],
       * });
       * ```
       */
      saveCorrections: async (
        id: string,
        attributes: AdminSaveCorrectionsAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionResult> => {
        return rb.execute<ExtractionResult>(
          patchAdminExtractionResultsByIdSaveCorrections,
          {
            path: { id },
            body: {
              data: { id, type: "extraction-result", attributes },
            },
          },
          options,
        );
      },

      /**
       * Lists all extraction results belonging to a specific workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction results for the workspace.
       *
       * @example
       * ```typescript
       * const results = await admin.extraction.results.listByWorkspace('ws-uuid');
       * ```
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionResult[]> => {
        return rb.execute<ExtractionResult[]>(
          getAdminExtractionResultsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
    },

    /** Batch upload management — create batches, get upload URLs. */
    batches: {
      /**
       * Retrieves a single extraction batch by its unique identifier.
       *
       * @param id - The UUID of the batch.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction batch object.
       *
       * @example
       * ```typescript
       * const batch = await admin.extraction.batches.get('batch-uuid');
       * console.log(`Batch: ${batch.name}, ${batch.document_count} docs`);
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionBatch> => {
        return rb.execute<ExtractionBatch>(
          getAdminExtractionBatchesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Permanently deletes an extraction batch.
       *
       * @param id - The UUID of the batch to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns `true` when the deletion succeeds.
       *
       * @example
       * ```typescript
       * await admin.extraction.batches.delete('batch-uuid');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminExtractionBatchesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new extraction batch for grouping document uploads.
       *
       * @param attributes - Batch creation attributes including workspace ID.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The newly created extraction batch object.
       *
       * @example
       * ```typescript
       * const batch = await admin.extraction.batches.create({
       *   name: 'Q1 Invoices',
       *   workspace_id: 'ws-uuid',
       *   agent_id: 'agent-uuid',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateBatchAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionBatch> => {
        return rb.execute<ExtractionBatch>(
          postAdminExtractionBatches,
          {
            body: {
              data: { type: "extraction-batch", attributes },
            },
          },
          options,
        );
      },

      /**
       * Lists all extraction batches belonging to a specific workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction batches for the workspace.
       *
       * @example
       * ```typescript
       * const batches = await admin.extraction.batches.listByWorkspace('ws-uuid');
       * ```
       */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionBatch[]> => {
        return rb.execute<ExtractionBatch[]>(
          getAdminExtractionBatchesWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Retrieves presigned upload URLs for all documents in a batch, enabling
       * parallel direct-to-storage uploads.
       *
       * @param id - The UUID of the batch.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of presigned URL objects, one per document slot.
       *
       * @example
       * ```typescript
       * const urls = await admin.extraction.batches.getUploadUrls('batch-uuid');
       * // Upload files in parallel to each presigned URL
       * await Promise.all(urls.map((u, i) => fetch(u.upload_url, {
       *   method: 'PUT',
       *   body: files[i],
       * })));
       * ```
       */
      getUploadUrls: async (
        id: string,
        options?: RequestOptions,
      ): Promise<PresignedUrl[]> => {
        return rb.execute<PresignedUrl[]>(
          getAdminExtractionBatchesByIdUploadUrls,
          { path: { id } },
          options,
        );
      },

      /**
       * Finish upload for all documents in a batch that are awaiting processing.
       *
       * Calls `:finish_upload` on each document in the batch that is still in
       * `:queued` status and returns processed count plus per-document results.
       *
       * @param batchId - The UUID of the extraction batch.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An operation result with processed count and per-document outcomes.
       *
       * @example
       * ```typescript
       * const result = await admin.extraction.batches.finishAll('batch-uuid');
       * console.log(result.processed_count);
       * ```
       */
      finishAll: async (
        batchId: string,
        options?: RequestOptions,
      ): Promise<AdminBatchFinishAllResult> => {
        const result = await rb.execute<
          AdminBatchFinishAllResult & {
            attributes?: AdminBatchFinishAllResult;
          }
        >(
          postAdminExtractionBatchesByIdFinishAll,
          {
            path: { id: batchId },
            body: { data: { batch_id: batchId } },
          },
          options,
        );

        return result.attributes ?? result;
      },
    },

    /** Export management — create and retrieve data exports. */
    exports: {
      /**
       * Lists all extraction exports for a workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction export objects.
       *
       * @example
       * ```typescript
       * const exports = await admin.extraction.exports.list('ws-uuid');
       * ```
       */
      list: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<ExtractionExport[]> => {
        return rb.execute<ExtractionExport[]>(
          getAdminWorkspacesByWorkspaceIdExtractionExports,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Creates a new data export for a workspace in the specified format.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param attributes - Export creation attributes including format.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The newly created extraction export object.
       *
       * @example
       * ```typescript
       * const exp = await admin.extraction.exports.create('ws-uuid', {
       *   format: 'csv',
       *   agent_id: 'agent-uuid',
       * });
       * console.log(`Export ${exp.id} created, status: ${exp.status}`);
       * ```
       */
      create: async (
        workspaceId: string,
        attributes: AdminCreateExportAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionExport> => {
        return rb.execute<ExtractionExport>(
          postAdminWorkspacesByWorkspaceIdExtractionExports,
          {
            path: { workspace_id: workspaceId },
            body: {
              data: { type: "extraction-export", attributes },
            },
          },
          options,
        );
      },

      /**
       * Retrieves a single extraction export by ID within a workspace.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param id - The UUID of the export to retrieve.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction export object, including download URL
       *   when the export is complete.
       *
       * @example
       * ```typescript
       * const exp = await admin.extraction.exports.get('ws-uuid', 'export-uuid');
       * if (exp.status === 'completed') {
       *   console.log(`Download: ${exp.download_url}`);
       * }
       * ```
       */
      get: async (
        workspaceId: string,
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionExport> => {
        return rb.execute<ExtractionExport>(
          getAdminWorkspacesByWorkspaceIdExtractionExportsById,
          { path: { workspace_id: workspaceId, id } },
          options,
        );
      },
    },

    /** Workflow management — configurable extraction pipelines. */
    workflows: {
      /**
       * Lists all extraction workflows across the platform.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction workflow objects.
       *
       * @example
       * ```typescript
       * const workflows = await admin.extraction.workflows.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ExtractionWorkflow[]> => {
        return rb.execute<ExtractionWorkflow[]>(
          getAdminExtractionWorkflows,
          {},
          options,
        );
      },

      /**
       * Creates a new extraction workflow with the specified configuration.
       *
       * @param attributes - Workflow creation attributes including name.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The newly created extraction workflow object.
       *
       * @example
       * ```typescript
       * const workflow = await admin.extraction.workflows.create({
       *   name: 'Invoice Processing',
       *   workspace_id: 'ws-uuid',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateWorkflowAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionWorkflow> => {
        return rb.execute<ExtractionWorkflow>(
          postAdminExtractionWorkflows,
          {
            body: {
              data: { type: "extraction-workflow", attributes },
            },
          },
          options,
        );
      },

      /**
       * Retrieves a single extraction workflow by its unique identifier.
       *
       * @param id - The UUID of the workflow.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction workflow object.
       *
       * @example
       * ```typescript
       * const workflow = await admin.extraction.workflows.get('workflow-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionWorkflow> => {
        return rb.execute<ExtractionWorkflow>(
          getAdminExtractionWorkflowsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Updates an existing extraction workflow's configuration.
       *
       * @param id - The UUID of the workflow to update.
       * @param attributes - The attributes to update.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated extraction workflow object.
       *
       * @example
       * ```typescript
       * const workflow = await admin.extraction.workflows.update('workflow-uuid', {
       *   name: 'Updated Invoice Pipeline',
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdateWorkflowAttributes,
        options?: RequestOptions,
      ): Promise<ExtractionWorkflow> => {
        return rb.execute<ExtractionWorkflow>(
          patchAdminExtractionWorkflowsById,
          {
            path: { id },
            body: {
              data: { id, type: "extraction-workflow", attributes },
            },
          },
          options,
        );
      },

      /**
       * Permanently deletes an extraction workflow.
       *
       * @param id - The UUID of the workflow to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns `true` when the deletion succeeds.
       *
       * @example
       * ```typescript
       * await admin.extraction.workflows.delete('workflow-uuid');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminExtractionWorkflowsById,
          { path: { id } },
          options,
        );
      },
    },

    /** Config enum management — dropdown and validation values. */
    configEnums: {
      /**
       * Lists all config enums used for extraction field validation and
       * dropdown options.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of config enum objects.
       *
       * @example
       * ```typescript
       * const enums = await admin.extraction.configEnums.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ConfigEnum[]> => {
        return rb.execute<ConfigEnum[]>(
          getAdminExtractionConfigEnums,
          {},
          options,
        );
      },

      /**
       * Creates a new config enum value for use in extraction field dropdowns
       * and validation.
       *
       * @param attributes - Config enum creation attributes.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The newly created config enum object.
       *
       * @example
       * ```typescript
       * const enumVal = await admin.extraction.configEnums.create({
       *   type: 'document_category',
       *   value: 'invoice',
       *   label: 'Invoice',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateConfigEnumAttributes,
        options?: RequestOptions,
      ): Promise<ConfigEnum> => {
        return rb.execute<ConfigEnum>(
          postAdminExtractionConfigEnums,
          {
            body: {
              data: { type: "config-enum", attributes },
            },
          },
          options,
        );
      },

      /**
       * Retrieves a single config enum by its unique identifier.
       *
       * @param id - The UUID of the config enum.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching config enum object.
       *
       * @example
       * ```typescript
       * const enumVal = await admin.extraction.configEnums.get('enum-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ConfigEnum> => {
        return rb.execute<ConfigEnum>(
          getAdminExtractionConfigEnumsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Updates an existing config enum's label or other mutable attributes.
       *
       * @param id - The UUID of the config enum to update.
       * @param attributes - The attributes to update.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated config enum object.
       *
       * @example
       * ```typescript
       * const enumVal = await admin.extraction.configEnums.update('enum-uuid', {
       *   label: 'Updated Label',
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdateConfigEnumAttributes,
        options?: RequestOptions,
      ): Promise<ConfigEnum> => {
        return rb.execute<ConfigEnum>(
          patchAdminExtractionConfigEnumsById,
          {
            path: { id },
            body: {
              data: { id, type: "config-enum", attributes },
            },
          },
          options,
        );
      },
    },

    /** Schema discovery — AI-powered field schema detection. */
    schemaDiscoveries: {
      /**
       * Runs schema discovery on documents to automatically detect extraction
       * field schemas using AI.
       *
       * @param attributes - Schema discovery attributes (document or workspace scope).
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The created schema discovery object with detected fields.
       *
       * @example
       * ```typescript
       * const discovery = await admin.extraction.schemaDiscoveries.create({
       *   document_id: 'doc-uuid',
       *   workspace_id: 'ws-uuid',
       * });
       * console.log(discovery.suggested_fields);
       * ```
       */
      create: async (
        attributes: AdminCreateSchemaDiscoveryAttributes,
        options?: RequestOptions,
      ): Promise<SchemaDiscovery> => {
        return rb.execute<SchemaDiscovery>(
          postAdminExtractionSchemaDiscoveries,
          {
            body: {
              data: { type: "schema-discovery", attributes },
            },
          },
          options,
        );
      },

      /**
       * Retrieves a schema discovery result by its unique identifier.
       *
       * @param id - The UUID of the schema discovery.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching schema discovery object.
       *
       * @example
       * ```typescript
       * const discovery = await admin.extraction.schemaDiscoveries.get('discovery-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<SchemaDiscovery> => {
        return rb.execute<SchemaDiscovery>(
          getAdminExtractionSchemaDiscoveriesById,
          { path: { id } },
          options,
        );
      },

      /**
       * Bootstraps schema discovery for a workspace, analyzing existing
       * documents to suggest an initial extraction schema.
       *
       * @param attributes - Bootstrap attributes (workspace scope).
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The bootstrapped schema discovery object.
       *
       * @example
       * ```typescript
       * const discovery = await admin.extraction.schemaDiscoveries.bootstrap({
       *   workspace_id: 'ws-uuid',
       * });
       * ```
       */
      bootstrap: async (
        attributes: AdminBootstrapSchemaDiscoveryAttributes,
        options?: RequestOptions,
      ): Promise<SchemaDiscovery> => {
        return rb.execute<SchemaDiscovery>(
          postAdminExtractionSchemaDiscoveriesBootstrap,
          {
            body: {
              data: { type: "schema-discovery", attributes },
            },
          },
          options,
        );
      },
    },

    /** Field mapping — document-to-schema field alignment. */
    fieldMappings: {
      /**
       * Retrieves the current field mapping for a document within a workspace,
       * showing how extracted fields align with the schema.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param documentId - The UUID of the document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The field mapping result for the document.
       *
       * @example
       * ```typescript
       * const mapping = await admin.extraction.fieldMappings.get('ws-uuid', 'doc-uuid');
       * console.log(mapping.mappings);
       * ```
       */
      get: async (
        workspaceId: string,
        documentId: string,
        options?: RequestOptions,
      ): Promise<FieldMappingResult> => {
        return rb.execute<FieldMappingResult>(
          getAdminWorkspacesByWorkspaceIdExtractionByDocumentIdMapping,
          { path: { workspace_id: workspaceId, document_id: documentId } },
          options,
        );
      },

      /**
       * Creates or updates the field mapping for a document, confirming how
       * extracted fields should map to schema fields.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param documentId - The UUID of the document.
       * @param attributes - The mapping attributes including confirmation and mappings.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The created or updated field mapping result.
       *
       * @example
       * ```typescript
       * const mapping = await admin.extraction.fieldMappings.create(
       *   'ws-uuid',
       *   'doc-uuid',
       *   {
       *     confirmed: true,
       *     mappings: [
       *       { source: 'inv_num', target: 'invoice_number' },
       *       { source: 'amt', target: 'total_amount' },
       *     ],
       *   },
       * );
       * ```
       */
      create: async (
        workspaceId: string,
        documentId: string,
        attributes: AdminFieldMappingAttributes,
        options?: RequestOptions,
      ): Promise<FieldMappingResult> => {
        return rb.execute<FieldMappingResult>(
          postAdminWorkspacesByWorkspaceIdExtractionByDocumentIdMapping,
          {
            path: { workspace_id: workspaceId, document_id: documentId },
            body: {
              data: { type: "field-mapping-confirmation", attributes },
            },
          },
          options,
        );
      },
    },

    /** Shadow comparison results — primary vs shadow extractor quality metrics. */
    shadowComparisons: {
      /**
       * Lists all shadow comparison records across the platform.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of shadow comparison objects.
       *
       * @example
       * ```typescript
       * const comparisons = await admin.extraction.shadowComparisons.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ShadowComparison[]> => {
        return rb.execute<ShadowComparison[]>(
          getAdminExtractionShadowComparisons,
          {},
          options,
        );
      },

      /**
       * Retrieves a single shadow comparison by its unique identifier.
       *
       * @param id - The UUID of the shadow comparison.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching shadow comparison object.
       *
       * @example
       * ```typescript
       * const comparison = await admin.extraction.shadowComparisons.get('comparison-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ShadowComparison> => {
        return rb.execute<ShadowComparison>(
          getAdminExtractionShadowComparisonsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new shadow comparison record to compare the output of the
       * primary extractor against a shadow extractor for quality evaluation.
       *
       * @param attributes - Shadow comparison creation attributes.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The newly created shadow comparison object.
       *
       * @example
       * ```typescript
       * const comparison = await admin.extraction.shadowComparisons.create({
       *   document_id: 'doc-uuid',
       *   workspace_id: 'ws-uuid',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreateShadowComparisonAttributes,
        options?: RequestOptions,
      ): Promise<ShadowComparison> => {
        return rb.execute<ShadowComparison>(
          postAdminExtractionShadowComparisons,
          {
            body: {
              data: { type: "shadow-comparison", attributes },
            },
          },
          options,
        );
      },

      /**
       * Permanently deletes a shadow comparison record.
       *
       * @param id - The UUID of the shadow comparison to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns `true` when the deletion succeeds.
       *
       * @example
       * ```typescript
       * await admin.extraction.shadowComparisons.delete('comparison-uuid');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminExtractionShadowComparisonsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Retrieves the aggregated analysis report comparing primary vs shadow
       * extractor performance across all shadow comparison records.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of shadow comparison analysis results.
       *
       * @example
       * ```typescript
       * const analysis = await admin.extraction.shadowComparisons.analysis();
       * ```
       */
      analysis: async (
        options?: RequestOptions,
      ): Promise<ShadowComparison[]> => {
        return rb.execute<ShadowComparison[]>(
          getAdminExtractionShadowComparisonsAnalysis,
          {},
          options,
        );
      },
    },

    /** Content analysis results — PII detection, OCR quality, entity extraction. */
    analyses: {
      /**
       * Lists all extraction analysis records across the platform, including
       * PII detection results and OCR quality metrics.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction analysis objects.
       *
       * @example
       * ```typescript
       * const analyses = await admin.extraction.analyses.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ExtractionAnalysis[]> => {
        return rb.execute<ExtractionAnalysis[]>(
          getAdminExtractionAnalyses,
          {},
          options,
        );
      },

      /**
       * Retrieves a single extraction analysis by its unique identifier.
       *
       * @param id - The UUID of the extraction analysis.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction analysis object.
       *
       * @example
       * ```typescript
       * const analysis = await admin.extraction.analyses.get('analysis-uuid');
       * console.log(analysis.pii_detected, analysis.ocr_confidence);
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionAnalysis> => {
        return rb.execute<ExtractionAnalysis>(
          getAdminExtractionAnalysesById,
          { path: { id } },
          options,
        );
      },
    },

    /** Post-processing hooks — configurable hooks that execute after extraction. */
    postProcessingHooks: {
      /**
       * Lists all post-processing hooks across the platform.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of post-processing hook objects.
       *
       * @example
       * ```typescript
       * const hooks = await admin.extraction.postProcessingHooks.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<PostProcessingHook[]> => {
        return rb.execute<PostProcessingHook[]>(
          getAdminPostProcessingHooks,
          {},
          options,
        );
      },

      /**
       * Retrieves a single post-processing hook by its unique identifier.
       *
       * @param id - The UUID of the hook.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching post-processing hook object.
       *
       * @example
       * ```typescript
       * const hook = await admin.extraction.postProcessingHooks.get('hook-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<PostProcessingHook> => {
        return rb.execute<PostProcessingHook>(
          getAdminPostProcessingHooksById,
          { path: { id } },
          options,
        );
      },

      /**
       * Creates a new post-processing hook for the extraction pipeline.
       *
       * @param attributes - Hook creation attributes including name, trigger, and type.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The newly created post-processing hook object.
       *
       * @example
       * ```typescript
       * const hook = await admin.extraction.postProcessingHooks.create({
       *   name: 'Notify on extraction',
       *   trigger: 'after_extraction',
       *   type: 'webhook',
       *   config: { url: 'https://example.com/hook' },
       *   workspace_id: 'ws-uuid',
       * });
       * ```
       */
      create: async (
        attributes: AdminCreatePostProcessingHookAttributes,
        options?: RequestOptions,
      ): Promise<PostProcessingHook> => {
        return rb.execute<PostProcessingHook>(
          postAdminPostProcessingHooks,
          {
            body: {
              data: { type: "post-processing-hook", attributes },
            },
          },
          options,
        );
      },

      /**
       * Updates an existing post-processing hook's configuration.
       *
       * @param id - The UUID of the hook to update.
       * @param attributes - The attributes to update.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The updated post-processing hook object.
       *
       * @example
       * ```typescript
       * const hook = await admin.extraction.postProcessingHooks.update('hook-uuid', {
       *   enabled: false,
       * });
       * ```
       */
      update: async (
        id: string,
        attributes: AdminUpdatePostProcessingHookAttributes,
        options?: RequestOptions,
      ): Promise<PostProcessingHook> => {
        return rb.execute<PostProcessingHook>(
          patchAdminPostProcessingHooksById,
          {
            path: { id },
            body: {
              data: { id, type: "post-processing-hook", attributes },
            },
          },
          options,
        );
      },

      /**
       * Permanently deletes a post-processing hook.
       *
       * @param id - The UUID of the hook to delete.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns `true` when the deletion succeeds.
       *
       * @example
       * ```typescript
       * await admin.extraction.postProcessingHooks.delete('hook-uuid');
       * ```
       */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminPostProcessingHooksById,
          { path: { id } },
          options,
        );
      },
    },

    /** Training analytics — workspace-level extraction training metrics. */
    trainingAnalytics: {
      /**
       * Retrieves training analytics for a specific workspace, including
       * accuracy trends, correction counts, and low-confidence documents.
       *
       * @param workspaceId - The UUID of the workspace.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The training analytics object for the workspace.
       *
       * @example
       * ```typescript
       * const analytics = await admin.extraction.trainingAnalytics.forWorkspace('ws-uuid');
       * console.log(analytics.avg_confidence, analytics.total_examples);
       * ```
       */
      forWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ): Promise<TrainingAnalytics> => {
        return rb.execute<TrainingAnalytics>(
          getAdminWorkspacesByWorkspaceIdTrainingAnalytics,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },

      /**
       * Retrieves training analytics for multiple workspaces in a single
       * request (max 50). Pass workspace IDs as a comma-separated string.
       *
       * @param workspaceIds - Comma-separated workspace UUIDs.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of training analytics objects.
       *
       * @example
       * ```typescript
       * const batch = await admin.extraction.trainingAnalytics.batch('ws-1,ws-2,ws-3');
       * ```
       */
      batch: async (
        workspaceIds: string,
        options?: RequestOptions,
      ): Promise<TrainingAnalytics[]> => {
        return rb.execute<TrainingAnalytics[]>(
          getAdminWorkspacesAnalyticsBatch,
          { query: { workspace_ids: workspaceIds } },
          options,
        );
      },
    },

    /** Extraction agents — list and predict best extraction agents for documents. */
    extractionAgents: {
      /**
       * Lists all available extraction agents (system agents tagged with
       * "extraction" category).
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of extraction agent objects.
       *
       * @example
       * ```typescript
       * const agents = await admin.extraction.extractionAgents.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ExtractionAgent[]> => {
        return rb.execute<ExtractionAgent[]>(
          getAdminExtractionAgents,
          {},
          options,
        );
      },

      /**
       * Retrieves a single extraction agent by its unique identifier.
       *
       * @param id - The UUID of the extraction agent.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching extraction agent object.
       *
       * @example
       * ```typescript
       * const agent = await admin.extraction.extractionAgents.get('agent-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ExtractionAgent> => {
        return rb.execute<ExtractionAgent>(
          getAdminExtractionAgentsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Predicts the best extraction agent for a given document based on its
       * name, description, or file content.
       *
       * @param attributes - Prediction input attributes.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns A prediction result with recommended agents.
       *
       * @example
       * ```typescript
       * const prediction = await admin.extraction.extractionAgents.predict({
       *   name: 'invoice.pdf',
       *   description: 'Monthly vendor invoice',
       *   file_content: 'Invoice #12345...',
       * });
       * ```
       */
      predict: async (
        attributes: AdminPredictExtractionAgentAttributes,
        options?: RequestOptions,
      ): Promise<unknown> => {
        const { name, description, file_content, document_id } = attributes;
        return rb.execute<unknown>(
          postAdminExtractionAgentsPredict,
          {
            body: {
              data: { name, description, file_content, document_id },
            },
          },
          options,
        );
      },
    },

    /** Chunk-entity links — bridge between document chunks and knowledge graph entities. */
    chunkEntityLinks: {
      /**
       * Lists all chunk-entity links across the platform.
       *
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of chunk-entity link objects.
       *
       * @example
       * ```typescript
       * const links = await admin.extraction.chunkEntityLinks.list();
       * ```
       */
      list: async (options?: RequestOptions): Promise<ChunkEntityLink[]> => {
        return rb.execute<ChunkEntityLink[]>(
          getAdminExtractionChunkEntityLinks,
          {},
          options,
        );
      },

      /**
       * Retrieves a single chunk-entity link by its unique identifier.
       *
       * @param id - The UUID of the chunk-entity link.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns The matching chunk-entity link object.
       *
       * @example
       * ```typescript
       * const link = await admin.extraction.chunkEntityLinks.get('link-uuid');
       * ```
       */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<ChunkEntityLink> => {
        return rb.execute<ChunkEntityLink>(
          getAdminExtractionChunkEntityLinksById,
          { path: { id } },
          options,
        );
      },

      /**
       * Lists all chunk-entity links for a specific document.
       *
       * @param documentId - The UUID of the document.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of chunk-entity links for the document.
       *
       * @example
       * ```typescript
       * const links = await admin.extraction.chunkEntityLinks.byDocument('doc-uuid');
       * ```
       */
      byDocument: async (
        documentId: string,
        options?: RequestOptions,
      ): Promise<ChunkEntityLink[]> => {
        return rb.execute<ChunkEntityLink[]>(
          getAdminExtractionChunkEntityLinksDocumentByDocumentId,
          { path: { document_id: documentId } },
          options,
        );
      },

      /**
       * Lists all chunk-entity links for a specific document chunk.
       *
       * @param documentChunkId - The UUID of the document chunk.
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of chunk-entity links for the chunk.
       *
       * @example
       * ```typescript
       * const links = await admin.extraction.chunkEntityLinks.byChunk('chunk-uuid');
       * ```
       */
      byChunk: async (
        documentChunkId: string,
        options?: RequestOptions,
      ): Promise<ChunkEntityLink[]> => {
        return rb.execute<ChunkEntityLink[]>(
          getAdminExtractionChunkEntityLinksChunkByDocumentChunkId,
          { path: { document_chunk_id: documentChunkId } },
          options,
        );
      },

      /**
       * Lists all chunk-entity links for a specific knowledge graph entity.
       *
       * @param graphNodeId - The UUID of the graph node (entity).
       * @param options - Optional request options (abort signal, custom headers).
       * @returns An array of chunk-entity links for the entity.
       *
       * @example
       * ```typescript
       * const links = await admin.extraction.chunkEntityLinks.byEntity('node-uuid');
       * ```
       */
      byEntity: async (
        graphNodeId: string,
        options?: RequestOptions,
      ): Promise<ChunkEntityLink[]> => {
        return rb.execute<ChunkEntityLink[]>(
          getAdminExtractionChunkEntityLinksEntityByGraphNodeId,
          { path: { graph_node_id: graphNodeId } },
          options,
        );
      },
    },
  };
}
